import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { useTranslation } from '../../i18n/useTranslation';
import { submitFeedback } from '../../services/appFeedbackService';
import { useFeedbackOptional } from './FeedbackContext';

/** Matches FEEDBACK_TEXT_MAX of the API (src/services/appFeedback.service.ts). */
export const FEEDBACK_TEXT_MAX = 5000;

type Status = 'idle' | 'sending' | 'sent' | 'error' | 'empty';

/**
 * "Feedback" dialog: what the user likes, which problems they see and what they
 * miss. Opened from the sidebar or automatically on every 10th login
 * (FeedbackContext); "Nicht mehr automatisch anzeigen" is stored per user.
 */
export function FeedbackDialog() {
  const feedback = useFeedbackOptional();
  const { t, i18n } = useTranslation();
  const formId = useId();
  const [liked, setLiked] = useState('');
  const [problems, setProblems] = useState('');
  const [missing, setMissing] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const noticeRef = useRef<HTMLDivElement>(null);
  const isOpen = feedback?.isOpen ?? false;

  // A fresh form every time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setLiked('');
    setProblems('');
    setMissing('');
    setStatus('idle');
  }, [isOpen]);

  useEffect(() => {
    if (status === 'error' || status === 'empty') noticeRef.current?.focus();
  }, [status]);

  if (feedback === null) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (status === 'sending') return;
    if ([liked, problems, missing].every((text) => text.trim() === '')) {
      setStatus('empty');
      return;
    }
    setStatus('sending');
    try {
      await submitFeedback({
        liked,
        problems,
        missing,
        source: feedback.source,
        locale: i18n.resolvedLanguage ?? i18n.language,
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const sent = status === 'sent';
  const footer = sent ? (
    <Button variant="primary" onClick={feedback.close}>
      {t('appFeedback.close')}
    </Button>
  ) : (
    <Inline gap="2" justify="end" wrap>
      <Button variant="ghost" onClick={feedback.close}>
        {feedback.source === 'prompt' ? t('appFeedback.later') : t('appFeedback.cancel')}
      </Button>
      <Button variant="primary" type="submit" form={formId} isLoading={status === 'sending'}>
        {t('appFeedback.submit')}
      </Button>
    </Inline>
  );

  return (
    <Dialog isOpen={isOpen} onClose={feedback.close} title={t('appFeedback.title')} size="md" footer={footer} data-testid="feedback-dialog">
      {sent ? (
        <Alert tone="success" title={t('appFeedback.sentTitle')} live="polite">
          {t('appFeedback.sentText')}
        </Alert>
      ) : (
        <form id={formId} onSubmit={(e) => void handleSubmit(e)} noValidate>
          <Stack gap="4">
            <Text>{t('appFeedback.intro')}</Text>
            {status === 'empty' && (
              <Alert tone="warning" ref={noticeRef} tabIndex={-1}>
                {t('appFeedback.emptyError')}
              </Alert>
            )}
            {status === 'error' && (
              <Alert tone="danger" ref={noticeRef} tabIndex={-1}>
                {t('appFeedback.sendError')}
              </Alert>
            )}
            <Textarea
              id={`${formId}-liked`}
              label={t('appFeedback.liked')}
              helperText={t('appFeedback.likedHint')}
              value={liked}
              onChange={(e) => setLiked(e.target.value)}
              maxLength={FEEDBACK_TEXT_MAX}
              rows={3}
              fullWidth
            />
            <Textarea
              id={`${formId}-problems`}
              label={t('appFeedback.problems')}
              helperText={t('appFeedback.problemsHint')}
              value={problems}
              onChange={(e) => setProblems(e.target.value)}
              maxLength={FEEDBACK_TEXT_MAX}
              rows={3}
              fullWidth
            />
            <Textarea
              id={`${formId}-missing`}
              label={t('appFeedback.missing')}
              helperText={t('appFeedback.missingHint')}
              value={missing}
              onChange={(e) => setMissing(e.target.value)}
              maxLength={FEEDBACK_TEXT_MAX}
              rows={3}
              fullWidth
            />
            <Checkbox
              label={t('appFeedback.optOut')}
              checked={feedback.optOut === true}
              disabled={feedback.optOut === null}
              onChange={(e) => void feedback.setOptOut(e.target.checked)}
            />
            {feedback.optOutSaveFailed && (
              <Alert tone="danger" compact>
                {t('appFeedback.optOutSaveFailed')}
              </Alert>
            )}
          </Stack>
        </form>
      )}
    </Dialog>
  );
}

export default FeedbackDialog;
